import { useState, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { cn } from '@/lib/cn';
import { productImageSrcSet } from '@/lib/cloudinary';
import { effectivePricePaise, type Product } from '@/types/product';
import { PriceDisplay } from '@/components/ui/PriceDisplay';
import { Badge } from '@/components/ui/Badge';
import { useAuthStore, selectIsAuthenticated } from '@/store/authStore';
import { useWishlistIds, useToggleWishlist } from '@/hooks/useWishlist';
import { toast } from '@/store/uiStore';

interface ProductCardProps {
  product: Product;
  index?: number;
  onQuickView?: (slug: string) => void;
  className?: string;
  /** When true (use for the first 4 cards in a grid), opts out of lazy loading
   *  and hints the browser to prioritise this image — critical for LCP. */
  priority?: boolean;
}

const NEW_THRESHOLD_DAYS = 14;

function isNew(createdAt: string): boolean {
  const created = new Date(createdAt).getTime();
  const ageDays = (Date.now() - created) / (1000 * 60 * 60 * 24);
  return ageDays <= NEW_THRESHOLD_DAYS;
}

// Drag-vs-click guard. Any ancestor (the card itself, or a carousel track) can
// opt out of navigation for the next click by setting
// `data-suppress-click="true"` — used by the horizontal New Arrivals carousel
// so swipes/drags don't accidentally open product details.
function suppressIfDragged(e: MouseEvent<HTMLAnchorElement>) {
  const blocked = (e.currentTarget as HTMLElement).closest('[data-suppress-click="true"]');
  if (blocked) {
    e.preventDefault();
    e.stopPropagation();
  }
}

export function ProductCard({ product, onQuickView, className, priority = false }: ProductCardProps) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const wishlistIds = useWishlistIds();
  const toggleWishlist = useToggleWishlist();
  const wishlisted = wishlistIds.has(product.id);
  const variant = product.variants?.[0];
  const finalPrice = effectivePricePaise(product, variant);
  const hasDiscount =
    product.discountedPrice !== null && product.discountedPrice < product.basePrice;
  const { src: imgUrl, srcSet: imgSrcSet } = productImageSrcSet(product.images, 400);
  const showNew = isNew(product.createdAt);
  const initials = product.name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

  const detailHref = `/products/${product.slug}`;

  const handleQuickView = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onQuickView?.(product.slug);
  };

  const handleWishlist = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      const next = encodeURIComponent(location.pathname + location.search);
      toast.info('Sign in to save items', 'Create a free account to build your wishlist.');
      navigate(`/login?next=${next}`);
      return;
    }
    toggleWishlist.mutate(product.id);
  };

  return (
    <article
      className={cn(
        'product-card group relative flex flex-col overflow-hidden rounded-lg',
        className
      )}
      data-product-slug={product.slug}
    >
      {/* Media container: holds the image, overlays, badges, link surface, and
       *  hover-revealed buttons. The Link is absolutely positioned over the
       *  whole media area so clicking the image (or initials fallback)
       *  navigates to the product page. */}
      <div className="relative aspect-[3/4] overflow-hidden rounded-lg bg-[color:var(--color-bg-surface)]">
        {/* Gradient + initials fallback (always rendered). */}
        <div
          aria-hidden
          className="absolute inset-0 grid place-items-center bg-gradient-to-br from-[color:var(--color-brand-navy)]/60 via-[color:var(--color-bg-surface)] to-[color:var(--color-bg-deep)]"
        >
          <span className="font-[family-name:var(--font-display)] text-5xl text-white/15">
            {initials}
          </span>
        </div>
        {imgUrl && !imgFailed && (
          <img
            src={imgUrl}
            srcSet={imgSrcSet || undefined}
            sizes="(min-width: 1280px) 22vw, (min-width: 1024px) 30vw, (min-width: 640px) 45vw, 92vw"
            alt=""
            width={400}
            height={500}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            decoding="async"
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgFailed(true)}
            draggable={false}
            className={cn(
              'relative h-full w-full object-cover transition-[transform,opacity] duration-700 ease-[cubic-bezier(0.25,0.1,0.25,1)] group-hover:scale-[1.08]',
              imgLoaded ? 'opacity-100' : 'opacity-0'
            )}
          />
        )}

        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[color:var(--color-bg-deep)]/65 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

        {/* Top-left badges (decorative, no pointer events). */}
        <div className="pointer-events-none absolute left-3 top-3 z-20 flex flex-col gap-1.5">
          {showNew && <Badge variant="gold">NEW</Badge>}
          {hasDiscount && <Badge variant="magenta">SALE</Badge>}
        </div>

        {/* Image link surface — covers the entire media area. Keyboard users
         *  reach the product via the visible title link below, so this one is
         *  removed from tab order to avoid duplicate stops. */}
        <Link
          to={detailHref}
          aria-label={`View ${product.name}`}
          tabIndex={-1}
          onClick={suppressIfDragged}
          className="absolute inset-0 z-10 block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand-magenta)]"
        />

        {/* Wishlist — above the link layer so it doesn't navigate. Fills
         *  magenta when the product is saved; toggles optimistically. */}
        <button
          type="button"
          aria-label={
            wishlisted
              ? `Remove ${product.name} from wishlist`
              : `Add ${product.name} to wishlist`
          }
          aria-pressed={wishlisted}
          onClick={handleWishlist}
          className={cn(
            'absolute right-3 top-3 z-30 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[color:var(--color-bg-deep)]/40 backdrop-blur-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand-magenta)]',
            wishlisted
              ? 'text-[color:var(--color-brand-magenta)] hover:bg-[color:var(--color-bg-deep)]/60'
              : 'text-white hover:bg-[color:var(--color-brand-magenta)]/80'
          )}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill={wishlisted ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </button>

        {/* Quick view — optional secondary CTA, hover-revealed at the bottom
         *  of the media area. Stops propagation so it doesn't trigger the
         *  image link. */}
        {onQuickView && (
          <motion.button
            type="button"
            onClick={handleQuickView}
            initial={{ y: 16, opacity: 0 }}
            whileHover={{ y: 0, opacity: 1 }}
            animate={{ y: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            className="absolute inset-x-3 bottom-3 z-30 inline-flex h-10 items-center justify-center rounded-md bg-white/95 text-[13px] font-medium uppercase tracking-[0.12em] text-[color:var(--color-bg-deep)] opacity-0 backdrop-blur-md transition-opacity duration-300 group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-brand-magenta)]"
            aria-label={`Quick view ${product.name}`}
          >
            Quick View
          </motion.button>
        )}
      </div>

      <div className="flex flex-col gap-1.5 p-3">
        {product.category && (
          <p className="text-label-mono text-[color:var(--color-text-muted)]">
            {product.category.name}
          </p>
        )}
        <h3 className="font-[family-name:var(--font-editorial)] text-[16px] font-semibold leading-tight text-white line-clamp-2">
          <Link
            to={detailHref}
            onClick={suppressIfDragged}
            className="outline-offset-2 transition-colors hover:text-[color:var(--color-brand-magenta)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--color-brand-magenta)]"
          >
            {product.name}
          </Link>
        </h3>
        <PriceDisplay
          paise={finalPrice}
          originalPaise={hasDiscount ? product.basePrice : null}
          gstRate={product.gstRate}
          size="sm"
          showGstNote={false}
        />
      </div>
    </article>
  );
}
